// src/pages/Recovery.tsx - Fixed version (only removed header notification)
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  submitRecovery,
  getRecoveryRequests,
} from "../redux/slices/recoverySlice";
import { getUnreadChatCount } from "../redux/slices/recoveryChatSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Shield,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Search,
  MessageSquare,
  Filter,
  Plus,
  Wallet as WalletIcon,
  Hash,
  ExternalLink,
  Copy,
  Check,
  Eye,
  X,
  Bell,
  Calendar,
} from "lucide-react";
import toast from "react-hot-toast";
import RecoveryChat from "../components/recovery/RecoveryChat";
import { formatCrypto, formatCurrency } from "../utils/currency";

interface RecoveryRequest {
  id: number;
  case_number: string;
  user_id: number;
  wallet_address: string;
  transaction_id: string;
  amount: number;
  currency: string;
  network: string;
  status: string;
  recovery_fee: number | null;
  success_fee_percentage: number;
  estimated_recovery_amount: number | null;
  recovery_plan: string | null;
  additional_notes: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  assessment_completed_at?: string;
  recovery_started_at?: string;
  recovery_completed_at?: string;
  estimated_completion_date?: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    verification_level: number;
  };
  // Add unread message count for this case
  unread_message_count?: number;
}

const Recovery: React.FC = () => {
  const dispatch = useDispatch();
  const { requests, isLoading } = useSelector(
    (state: RootState) => state.recovery
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    wallet_address: "",
    transaction_id: "",
    amount: "",
    currency: "BTC",
    network: "Bitcoin",
    additional_notes: "",
  });

  const [filter, setFilter] = useState({
    search: "",
    status: "",
    network: "",
    date_from: "",
    date_to: "",
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedCase, setSelectedCase] = useState<RecoveryRequest | null>(
    null
  );
  const [showCaseDetails, setShowCaseDetails] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showChatModal, setShowChatModal] = useState<{
    isOpen: boolean;
    caseId: number;
    caseNumber: string;
  }>({
    isOpen: false,
    caseId: 0,
    caseNumber: "",
  });

  // Accurate cryptocurrency and network mappings
  const currencyOptions = [
    {
      value: "BTC",
      label: "Bitcoin (BTC)",
      networks: ["Bitcoin", "Lightning"],
    },
    {
      value: "ETH",
      label: "Ethereum (ETH)",
      networks: ["Ethereum", "Ethereum 2.0"],
    },
    {
      value: "USDT",
      label: "Tether (USDT)",
      networks: ["Ethereum", "Tron", "Solana", "BNB Smart Chain"],
    },
    {
      value: "BNB",
      label: "BNB (BNB)",
      networks: ["BNB Smart Chain", "BNB Beacon Chain"],
    },
    { value: "XRP", label: "Ripple (XRP)", networks: ["XRP Ledger"] },
    { value: "ADA", label: "Cardano (ADA)", networks: ["Cardano"] },
    { value: "SOL", label: "Solana (SOL)", networks: ["Solana"] },
    { value: "DOT", label: "Polkadot (DOT)", networks: ["Polkadot"] },
    { value: "DOGE", label: "Dogecoin (DOGE)", networks: ["Dogecoin"] },
    { value: "TRX", label: "TRON (TRX)", networks: ["TRON"] },
    { value: "MATIC", label: "Polygon (MATIC)", networks: ["Polygon"] },
    { value: "LTC", label: "Litecoin (LTC)", networks: ["Litecoin"] },
    { value: "SHIB", label: "Shiba Inu (SHIB)", networks: ["Ethereum"] },
    {
      value: "AVAX",
      label: "Avalanche (AVAX)",
      networks: ["Avalanche C-Chain", "Avalanche X-Chain", "Avalanche P-Chain"],
    },
    { value: "LINK", label: "Chainlink (LINK)", networks: ["Ethereum"] },
    { value: "UNI", label: "Uniswap (UNI)", networks: ["Ethereum"] },
    { value: "ATOM", label: "Cosmos (ATOM)", networks: ["Cosmos"] },
    { value: "XMR", label: "Monero (XMR)", networks: ["Monero"] },
  ];

  // Network options based on selected currency
  const getNetworkOptions = () => {
    const selectedCurrency = currencyOptions.find(
      (c) => c.value === formData.currency
    );
    return selectedCurrency ? selectedCurrency.networks : ["Bitcoin"];
  };

  // Update network when currency changes
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = e.target.value;
    const currency = currencyOptions.find((c) => c.value === newCurrency);
    const defaultNetwork = currency ? currency.networks[0] : "Bitcoin";

    setFormData({
      ...formData,
      currency: newCurrency,
      network: defaultNetwork,
    });
  };

  useEffect(() => {
    dispatch(getRecoveryRequests({}) as any);
    if (user) {
      dispatch(getUnreadChatCount() as any);
    }
  }, [dispatch, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.wallet_address ||
      !formData.transaction_id ||
      !formData.amount
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      await dispatch(
        submitRecovery({
          ...formData,
          amount: parseFloat(formData.amount),
        }) as any
      );
      toast.success("Recovery request submitted successfully!");
      setShowForm(false);
      setFormData({
        wallet_address: "",
        transaction_id: "",
        amount: "",
        currency: "BTC",
        network: "Bitcoin",
        additional_notes: "",
      });
    } catch (error) {
      toast.error("Failed to submit recovery request");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "in_progress":
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return GlobalStyles.badgeSuccess;
      case "in_progress":
      case "under_review":
      case "assessment_complete":
      case "recovery_plan_sent":
      case "recovery_approved":
        return GlobalStyles.badgeInfo;
      case "failed":
        return GlobalStyles.badgeError;
      case "cancelled":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      default:
        return GlobalStyles.badgeWarning;
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const filteredRequests = requests.filter((request: RecoveryRequest) => {
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return (
        request.case_number?.toLowerCase().includes(searchLower) ||
        request.wallet_address?.toLowerCase().includes(searchLower) ||
        request.transaction_id?.toLowerCase().includes(searchLower) ||
        request.currency?.toLowerCase().includes(searchLower)
      );
    }

    if (filter.status && request.status !== filter.status) {
      return false;
    }

    if (filter.network && request.network !== filter.network) {
      return false;
    }

    if (filter.date_from || filter.date_to) {
      const createdDate = new Date(request.created_at);
      const fromDate = filter.date_from ? new Date(filter.date_from) : null;
      const toDate = filter.date_to ? new Date(filter.date_to) : null;

      if (fromDate && createdDate < fromDate) return false;
      if (toDate && createdDate > toDate) return false;
    }

    return true;
  });

  // Calculate total unread messages across all cases
  const totalUnreadMessages = requests.reduce(
    (total: number, request: RecoveryRequest) =>
      total + (request.unread_message_count || 0),
    0
  );

  // Calculate cases with unread messages
  const casesWithUnreadMessages = requests.filter(
    (request: RecoveryRequest) => (request.unread_message_count || 0) > 0
  ).length;

  const calculateStatistics = () => {
    const totalCases = requests.length;
    const activeCases = requests.filter((r: RecoveryRequest) =>
      [
        "submitted",
        "under_review",
        "assessment_complete",
        "recovery_plan_sent",
        "recovery_approved",
        "in_progress",
      ].includes(r.status)
    ).length;

    const completedCases = requests.filter(
      (r: RecoveryRequest) => r.status === "completed"
    ).length;

    const totalAmount = requests.reduce(
      (sum: number, r: RecoveryRequest) => sum + r.amount,
      0
    );
    const estimatedRecovery = requests.reduce(
      (sum: number, r: RecoveryRequest) =>
        sum + (r.estimated_recovery_amount || 0),
      0
    );

    const successRate =
      totalCases > 0 ? Math.round((completedCases / totalCases) * 100) : 0;

    return {
      totalCases,
      activeCases,
      completedCases,
      totalAmount,
      estimatedRecovery,
      successRate,
      totalUnreadMessages,
      casesWithUnreadMessages,
    };
  };

  const stats = calculateStatistics();

  const openCaseDetails = (caseItem: RecoveryRequest) => {
    setSelectedCase(caseItem);
    setShowCaseDetails(true);
  };

  const closeCaseDetails = () => {
    setSelectedCase(null);
    setShowCaseDetails(false);
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`Copied ${field} to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const getExplorerUrl = (network: string, transactionId: string) => {
    const explorers: Record<string, string> = {
      Bitcoin: `https://blockchain.info/tx/${transactionId}`,
      Ethereum: `https://etherscan.io/tx/${transactionId}`,
      "Binance Smart Chain": `https://bscscan.com/tx/${transactionId}`,
      BSC: `https://bscscan.com/tx/${transactionId}`,
      Solana: `https://solscan.io/tx/${transactionId}`,
      Polygon: `https://polygonscan.com/tx/${transactionId}`,
      Arbitrum: `https://arbiscan.io/tx/${transactionId}`,
      "BNB Smart Chain": `https://bscscan.com/tx/${transactionId}`,
      TRON: `https://tronscan.org/#/transaction/${transactionId}`,
      Cardano: `https://cardanoscan.io/transaction/${transactionId}`,
      Litecoin: `https://blockchair.com/litecoin/transaction/${transactionId}`,
      Dogecoin: `https://blockchair.com/dogecoin/transaction/${transactionId}`,
    };
    return (
      explorers[network] || `https://blockchair.com/search?q=${transactionId}`
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  const clearFilters = () => {
    setFilter({
      search: "",
      status: "",
      network: "",
      date_from: "",
      date_to: "",
    });
  };

  if (!user || user.verification_level < 3) {
    return (
      <div className={GlobalStyles.pageContainer}>
        <div className={GlobalStyles.contentContainer}>
          <div className={GlobalStyles.card}>
            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-10 w-10 text-red-500" />
              </div>
              <h3 className={GlobalStyles.heading2 + " mb-2"}>
                KYC Verification Required
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You need to complete KYC Level 3 verification to access recovery
                services.
              </p>
              <button
                onClick={() => (window.location.href = "/profile")}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
              >
                Complete KYC
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header - REMOVED UNREAD COUNT ICON */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>Fund Recovery</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                Recover lost or stolen cryptocurrency funds
                {stats.totalUnreadMessages > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    <Bell className="h-3 w-3" />
                    {stats.totalUnreadMessages} unread message
                    {stats.totalUnreadMessages !== 1 ? "s" : ""} across{" "}
                    {stats.casesWithUnreadMessages} case
                    {stats.casesWithUnreadMessages !== 1 ? "s" : ""}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* REMOVED THE UNREAD COUNT ICON FROM HEADER */}
              <button
                onClick={() => setShowForm(!showForm)}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge}`}
              >
                <Plus className="h-5 w-5 mr-2" />
                New Recovery Request
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div
          className={`${GlobalStyles.gridCols4} gap-6 ${GlobalStyles.sectionSpacing}`}
        >
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Cases
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {stats.totalCases}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {stats.activeCases} active
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Success Rate
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {stats.successRate}%
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {stats.completedCases} completed
                </p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Recovered
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {formatCurrency(stats.estimatedRecovery * 45000)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Estimated value
                </p>
              </div>
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Unread Messages
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {stats.totalUnreadMessages}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {stats.casesWithUnreadMessages} case
                  {stats.casesWithUnreadMessages !== 1 ? "s" : ""} need
                  attention
                </p>
              </div>
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20">
                <Bell className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Recovery Form */}
        {showForm && (
          <div
            className={`${GlobalStyles.card} ${GlobalStyles.sectionSpacing}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className={GlobalStyles.heading2}>New Recovery Request</h3>
              <button
                onClick={() => setShowForm(false)}
                className={GlobalStyles.iconButton}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Wallet Address */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Wallet Address *
                  </label>
                  <input
                    type="text"
                    value={formData.wallet_address}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        wallet_address: e.target.value,
                      })
                    }
                    className={GlobalStyles.input}
                    placeholder="Enter the wallet address where funds were sent..."
                    required
                  />
                </div>

                {/* Transaction ID */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Transaction ID/Hash *
                  </label>
                  <input
                    type="text"
                    value={formData.transaction_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        transaction_id: e.target.value,
                      })
                    }
                    className={GlobalStyles.input}
                    placeholder="Enter the transaction hash/ID..."
                    required
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Amount Lost *
                  </label>
                  <input
                    type="number"
                    step="0.00000001"
                    min="0.00000001"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    className={GlobalStyles.input}
                    placeholder="0.00"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Enter the exact amount lost
                  </p>
                </div>

                {/* Currency - Updated with accurate options */}
                <div>
                  <label className={GlobalStyles.inputLabel}>Currency *</label>
                  <select
                    value={formData.currency}
                    onChange={handleCurrencyChange}
                    className={GlobalStyles.input}
                    required
                  >
                    {currencyOptions.map((currency) => (
                      <option key={currency.value} value={currency.value}>
                        {currency.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Network - Dynamic based on currency */}
                <div>
                  <label className={GlobalStyles.inputLabel}>Network *</label>
                  <select
                    value={formData.network}
                    onChange={(e) =>
                      setFormData({ ...formData, network: e.target.value })
                    }
                    className={GlobalStyles.input}
                    required
                  >
                    {getNetworkOptions().map((network) => (
                      <option key={network} value={network}>
                        {network}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Network where the transaction occurred
                  </p>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className={GlobalStyles.inputLabel}>
                  Additional Notes
                </label>
                <textarea
                  value={formData.additional_notes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      additional_notes: e.target.value,
                    })
                  }
                  className={`${GlobalStyles.input} min-h-[100px] resize-none`}
                  placeholder="Provide any additional information about the incident, such as:
• How the funds were lost (hack, scam, wrong address, etc.)
• Date and time of the transaction
• Any relevant communication with the recipient
• Steps already taken to recover funds"
                />
              </div>

              {/* Two-Tier Fee Notice */}
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo}`}
              >
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Two-Tier Fee Structure</p>
                  <p className="text-sm mt-1">
                    Our fee structure includes an upfront 20% administrative
                    fee, followed by a 10% success fee upon recovery, for a
                    total of 30% of the recovered amount.
                  </p>
                  <div className="mt-2 space-y-1 text-xs">
                    <p>
                      <strong>• Upfront Fee (20%):</strong> Required to initiate
                      the recovery process and cover administrative costs.
                    </p>
                    <p>
                      <strong>• Success Fee (10%):</strong> Payable only upon
                      successful recovery of your funds.
                    </p>
                    <p>
                      <strong>• Total Fee:</strong> 30% of the recovered amount
                      (20% + 10%).
                    </p>
                  </div>
                  <p className="text-xs mt-2 text-amber-600 dark:text-amber-400">
                    <strong>Note:</strong> The 20% upfront fee is non-refundable
                    and required to begin the recovery process. The remaining
                    10% is deducted from the successfully recovered funds.
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonMedium}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                      ></div>
                      Submitting...
                    </div>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className={`${GlobalStyles.card} ${GlobalStyles.sectionSpacing}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={GlobalStyles.heading3}>Filters</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1 text-sm text-primary"
              >
                <Filter className="h-4 w-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={GlobalStyles.inputLabel}>Status</label>
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={`${GlobalStyles.input} min-w-[150px]`}
              >
                <option value="">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="assessment_complete">Assessment Complete</option>
                <option value="recovery_plan_sent">Recovery Plan Sent</option>
                <option value="recovery_approved">Recovery Approved</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className={GlobalStyles.inputLabel}>Network</label>
              <select
                value={filter.network}
                onChange={(e) =>
                  setFilter({ ...filter, network: e.target.value })
                }
                className={`${GlobalStyles.input} min-w-[150px]`}
              >
                <option value="">All Networks</option>
                <option value="Bitcoin">Bitcoin</option>
                <option value="Ethereum">Ethereum</option>
                <option value="Binance Smart Chain">Binance Smart Chain</option>
                <option value="Solana">Solana</option>
                <option value="Polygon">Polygon</option>
                <option value="Arbitrum">Arbitrum</option>
                <option value="TRON">TRON</option>
                <option value="Cardano">Cardano</option>
              </select>
            </div>
            <div>
              <label className={GlobalStyles.inputLabel}>Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by case number, wallet, or transaction ID..."
                  value={filter.search}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value })
                  }
                  className={`${GlobalStyles.input} pl-10`}
                />
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <label className={GlobalStyles.inputLabel}>Date From</label>
                <input
                  type="date"
                  value={filter.date_from}
                  onChange={(e) =>
                    setFilter({ ...filter, date_from: e.target.value })
                  }
                  className={GlobalStyles.input}
                />
              </div>
              <div>
                <label className={GlobalStyles.inputLabel}>Date To</label>
                <input
                  type="date"
                  value={filter.date_to}
                  onChange={(e) =>
                    setFilter({ ...filter, date_to: e.target.value })
                  }
                  className={GlobalStyles.input}
                />
              </div>
            </div>
          )}
        </div>

        {/* Recovery Cases */}
        <div className={GlobalStyles.card}>
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <h3 className={GlobalStyles.heading2}>My Recovery Cases</h3>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              <button
                onClick={() => dispatch(getRecoveryRequests({}) as any)}
                disabled={isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall}`}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>

          {filteredRequests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className={GlobalStyles.table}>
                <thead>
                  <tr className={`${GlobalStyles.tableHeader} text-left`}>
                    <th className={GlobalStyles.tableCell}>Case #</th>
                    <th className={`${GlobalStyles.tableCell} min-w-[120px]`}>
                      Chat
                    </th>
                    <th className={GlobalStyles.tableCell}>Amount</th>
                    <th className={GlobalStyles.tableCell}>Currency/Network</th>
                    <th className={GlobalStyles.tableCell}>Status</th>
                    <th className={GlobalStyles.tableCell}>Date Submitted</th>
                    <th className={GlobalStyles.tableCell}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((request: RecoveryRequest) => (
                    <tr key={request.id} className={GlobalStyles.tableRow}>
                      <td className={GlobalStyles.tableCell}>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {request.case_number || `CASE-${request.id}`}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]">
                          {request.wallet_address}
                        </div>
                      </td>
                      <td className={`${GlobalStyles.tableCell} min-w-[120px]`}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              setShowChatModal({
                                isOpen: true,
                                caseId: request.id,
                                caseNumber:
                                  request.case_number || `CASE-${request.id}`,
                              })
                            }
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} flex items-center gap-1 relative`}
                            title="Open Chat"
                          >
                            <MessageSquare className="h-3 w-3" />
                            Chat
                            {request.unread_message_count &&
                              request.unread_message_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {request.unread_message_count}
                                </span>
                              )}
                          </button>
                          {request.unread_message_count &&
                          request.unread_message_count > 0 ? (
                            <span className="md:hidden flex items-center gap-1">
                              <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {request.unread_message_count}
                              </span>
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {formatCrypto(request.amount, request.currency)}
                        </div>
                        {request.estimated_recovery_amount && (
                          <div className="text-sm text-green-600 dark:text-green-400">
                            Est. recovery:{" "}
                            {formatCrypto(
                              request.estimated_recovery_amount,
                              request.currency
                            )}
                          </div>
                        )}
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className="flex flex-col gap-1">
                          <span
                            className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary}`}
                          >
                            {request.currency}
                          </span>
                          <span
                            className={`text-sm text-gray-500 dark:text-gray-400 text-center`}
                          >
                            {request.network}
                          </span>
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className="flex items-center">
                          {getStatusIcon(request.status)}
                          <span
                            className={`${
                              GlobalStyles.badge
                            } ml-2 ${getStatusColor(request.status)}`}
                          >
                            {formatStatus(request.status)}
                          </span>
                        </div>
                        {request.estimated_completion_date && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Est. completion:{" "}
                            {new Date(
                              request.estimated_completion_date
                            ).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(request.created_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </td>
                      <td className={`${GlobalStyles.tableCell} min-w-[200px]`}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openCaseDetails(request)}
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} flex items-center gap-1`}
                            title="View Details"
                          >
                            <Eye className="h-3 w-3" />
                            Details
                          </button>
                          {request.transaction_id && (
                            <a
                              href={getExplorerUrl(
                                request.network,
                                request.transaction_id
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} flex items-center gap-1`}
                              title="View on Explorer"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Recovery Cases
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Submit your first recovery request to get started
              </p>
              <button
                onClick={() => setShowForm(true)}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
              >
                Start Recovery Case
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Case Details Modal */}
      {showCaseDetails && selectedCase && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeCaseDetails}
            />

            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${getStatusColor(
                      selectedCase.status
                    )}`}
                  >
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Case Details: {selectedCase.case_number}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                          selectedCase.status
                        )}`}
                      >
                        {formatStatus(selectedCase.status)}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        • Submitted {formatDate(selectedCase.created_at)}
                      </span>
                      {selectedCase.unread_message_count &&
                        selectedCase.unread_message_count > 0 && (
                          <span className="flex items-center gap-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                            <Bell className="h-3 w-3" />
                            {selectedCase.unread_message_count} new message
                            {selectedCase.unread_message_count !== 1 ? "s" : ""}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={closeCaseDetails}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div
                className="overflow-y-auto p-6"
                style={{ maxHeight: "calc(90vh - 120px)" }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Case Info */}
                    <div className={GlobalStyles.card}>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        Case Information
                      </h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Case Number
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedCase.case_number}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Status
                            </p>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                                selectedCase.status
                              )}`}
                            >
                              {formatStatus(selectedCase.status)}
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Network
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedCase.network}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Currency
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedCase.currency}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Submission Date
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatDateTime(selectedCase.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Last Updated
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatDateTime(selectedCase.updated_at)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Transaction Info */}
                    <div className={GlobalStyles.card}>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                        <Hash className="h-4 w-4 mr-2" />
                        Transaction Details
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Transaction ID
                          </p>
                          <div className="flex items-center justify-between group">
                            <p className="font-mono text-sm truncate max-w-[70%] text-gray-900 dark:text-white">
                              {selectedCase.transaction_id}
                            </p>
                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() =>
                                  handleCopy(
                                    selectedCase.transaction_id,
                                    "Transaction ID"
                                  )
                                }
                                title="Copy transaction ID"
                              >
                                {copiedField === "Transaction ID" ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                )}
                              </button>
                              <a
                                href={getExplorerUrl(
                                  selectedCase.network,
                                  selectedCase.transaction_id
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-600"
                                title="View on explorer"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Amount Lost
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formatCrypto(
                                selectedCase.amount,
                                selectedCase.currency
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              USD Value
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              ~${formatCurrency(selectedCase.amount * 45000)}
                            </p>
                          </div>
                        </div>
                        {selectedCase.additional_notes && (
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Your Notes
                            </p>
                            <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {selectedCase.additional_notes}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Wallet Info */}
                    <div className={GlobalStyles.card}>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                        <WalletIcon className="h-4 w-4 mr-2" />
                        Wallet Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Wallet Address
                          </p>
                          <div className="flex items-center justify-between group">
                            <p className="font-mono text-sm truncate max-w-[80%] text-gray-900 dark:text-white">
                              {selectedCase.wallet_address}
                            </p>
                            <button
                              onClick={() =>
                                handleCopy(
                                  selectedCase.wallet_address,
                                  "Wallet Address"
                                )
                              }
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Copy address"
                            >
                              {copiedField === "Wallet Address" ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Recovery Info */}
                    <div className={GlobalStyles.card}>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                        <DollarSign className="h-4 w-4 mr-2" />
                        Recovery Information
                      </h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Estimated Recovery
                            </p>
                            <p className="font-medium text-green-600 dark:text-green-400">
                              {selectedCase.estimated_recovery_amount
                                ? formatCrypto(
                                    selectedCase.estimated_recovery_amount,
                                    selectedCase.currency
                                  )
                                : "—"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Success Fee
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedCase.success_fee_percentage > 0
                                ? `${selectedCase.success_fee_percentage}%`
                                : "—"}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Recovery Fee
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedCase.recovery_fee
                                ? formatCrypto(
                                    selectedCase.recovery_fee,
                                    selectedCase.currency
                                  )
                                : "—"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Net Recovery
                            </p>
                            <p className="font-medium text-green-600 dark:text-green-400">
                              {selectedCase.estimated_recovery_amount &&
                              selectedCase.recovery_fee
                                ? formatCrypto(
                                    selectedCase.estimated_recovery_amount -
                                      selectedCase.recovery_fee,
                                    selectedCase.currency
                                  )
                                : "—"}
                            </p>
                          </div>
                        </div>
                        {selectedCase.admin_notes && (
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Admin Notes
                            </p>
                            <div className="mt-1 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {selectedCase.admin_notes}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Recovery Timeline */}
                    <div className={GlobalStyles.card}>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        Recovery Timeline
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Submitted:
                          </span>
                          <span className="font-medium">
                            {formatDateTime(selectedCase.created_at)}
                          </span>
                        </div>
                        {selectedCase.assessment_completed_at && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                              Assessment Completed:
                            </span>
                            <span className="font-medium">
                              {formatDateTime(
                                selectedCase.assessment_completed_at
                              )}
                            </span>
                          </div>
                        )}
                        {selectedCase.recovery_started_at && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                              Recovery Started:
                            </span>
                            <span className="font-medium">
                              {formatDateTime(selectedCase.recovery_started_at)}
                            </span>
                          </div>
                        )}
                        {selectedCase.recovery_completed_at && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                              Recovery Completed:
                            </span>
                            <span className="font-medium">
                              {formatDateTime(
                                selectedCase.recovery_completed_at
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recovery Plan */}
                {selectedCase.recovery_plan && (
                  <div className={GlobalStyles.card + " mt-6"}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                      Recovery Plan
                    </h4>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {selectedCase.recovery_plan}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal.isOpen && (
        <RecoveryChat
          recoveryId={showChatModal.caseId}
          caseNumber={showChatModal.caseNumber}
          isAdmin={false} // This is correct for user
          onClose={() => {
            setShowChatModal({ isOpen: false, caseId: 0, caseNumber: "" });
            dispatch(getRecoveryRequests({}) as any);
            dispatch(getUnreadChatCount() as any);
          }}
          isOpen={showChatModal.isOpen}
        />
      )}
    </div>
  );
};

export default Recovery;
